import { forwardRef, useContext, MouseEvent } from "react";
import { StepperContext } from "./Stepper";
import { Button, ButtonProps } from "../Button";

/**
 * Props for the StepperNextButton component
 * @extends ButtonProps
 */
export type StepperNextButtonProps = Omit<ButtonProps, "children"> & {
  /**
   * Label for the next button.
   * @default "Next"
   */
  nextLabel?: string;

  /**
   * Label for the complete button (shown on the last step).
   * @default "Complete"
   */
  completeLabel?: string;

  /**
   * Called when the "Next" button is clicked.
   * @param e MouseEvent<HTMLButtonElement>
   * @returns If the function returns `false`, or a Promise that resolves to `false`, the next step will not be executed.
   */
  onClick?: (
    e: MouseEvent<HTMLButtonElement>,
  ) => void | boolean | Promise<boolean>;
};

/**
 * Next button component for navigating to the next step in the Stepper.
 *
 * Features:
 * - Automatically advances to the next step
 * - Changes label to "Complete" on the final step
 * - Supports async validation with Promise return
 * - Prevents navigation when validation fails
 * - Hides when all steps are completed
 * - Accessible with proper button semantics
 * - Customizable appearance and styling
 * - Integration with stepper context
 * - Automatic completion handling
 *
 * @example
 * <Stepper.NextButton
 *   nextLabel="Continue"
 *   completeLabel="Finish"
 * />
 *
 * @example
 * <Stepper.NextButton
 *   onClick={async (e) => {
 *     const isValid = await validateForm();
 *     return isValid;
 *   }}
 *   appearance="primary"
 * />
 */
export const StepperNextButton = forwardRef<
  HTMLButtonElement,
  StepperNextButtonProps
>((props, ref) => {
  const {
    setAllCompleted,
    onComplete,
    setCurrent,
    setFocus,
    items,
    current,
    allCompleted,
  } = useContext(StepperContext);
  const {
    onClick,
    onFocus,
    nextLabel = "Next",
    completeLabel = "Complete",
    appearance = "primary",
    ...rest
  } = props;

  const proceedToNextStep = () => {
    if (items && current === items.length - 1) {
      items.forEach((item) => item.setAttribute("tabindex", "-1"));
      setAllCompleted?.(true);
      onComplete?.();
      return;
    }
    setCurrent(current + 1);
    setFocus(current + 1);
  };

  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    const onClickResult = onClick?.(e);

    // check if onClick returns a Promise
    if (
      onClickResult !== undefined &&
      !!onClickResult &&
      (onClickResult as unknown) instanceof Promise
    ) {
      (onClickResult as unknown as Promise<boolean>).then((result) => {
        if (result === false) return;
        proceedToNextStep();
      });

      // otherwise, onClick returns a boolean or void
    } else {
      if ((onClickResult as void | boolean) === false) return;
      proceedToNextStep();
    }
  };

  return allCompleted ? null : (
    <Button
      data-anv="stepper-next-button"
      onClick={onClickHandler}
      appearance={appearance}
      ref={ref}
      {...rest}
    >
      {items && current === items.length - 1 ? completeLabel : nextLabel}
    </Button>
  );
});

StepperNextButton.displayName = "StepperNextButton";
